import type { BCCADMINBaseModelsStorageBrowseResponse, StorageFolder, StorageObject } from "../../API";
import type { MediaOrphanFileDto, MediaOrphanReportDto, MediaOrphanUsageDto } from "../Checks/checksCommon";
import { resolveStorageUrl as resolveStorageUrlHelper } from "../../Helper/storage";
import { parseApiDate } from "../../Helper/dates";
import type { BrowseState, SortDir, SortKey, UploadKind } from "./types";
import { BROWSE_PAGE_SIZE, PUBLICATION_EXTENSIONS } from "./types";

export const normalizePath = (value?: string | null): string | null => {
	if (!value) {
		return null;
	}
	const sanitized = value.replace(/\\/g, "/").trim();
	if (!sanitized) {
		return null;
	}
	const trimmed = sanitized.replace(/^\/+|\/+$/g, "").replace(/\/{2,}/g, "/");
	return trimmed || null;
};

export const coerceList = <T,>(items?: Array<T | null> | null): T[] => {
	if (!Array.isArray(items)) {
		return [];
	}
	return items.filter((item): item is T => Boolean(item));
};

export const createBrowseStateFromResponse = (response?: BCCADMINBaseModelsStorageBrowseResponse | null): BrowseState => ({
	folder: response?.folder ?? null,
	breadcrumbs: coerceList<StorageFolder>(response?.breadcrumbs),
	children: coerceList<StorageFolder>(response?.children),
	objects: coerceList<StorageObject>(response?.objects),
	page: response?.page ?? 1,
	pageSize: response?.pageSize ?? BROWSE_PAGE_SIZE,
	hasMore: response?.hasMore ?? false,
	totalObjects: response?.totalObjects ?? 0
});

export const isSystemKind = (kind?: string | null) => ["content", "layout"].includes((kind ?? "").toLowerCase());

export const buildPublicUrl = (key?: string | null): string | null => {
	if (!key) return null;
	const resolved = resolveStorageUrlHelper(key);
	return resolved ? resolved : null;
};

const normalizeStorageKey = (key?: string | null): string => (key ?? "").trim().replace(/^\/+/, "");

export const resolveCompressedKey = (object?: StorageObject | null): string | null => {
	const direct = normalizeStorageKey(object?.compressedKey);
	return direct || null;
};

export const resolveOriginalKey = (object?: StorageObject | null): string | null => {
	const direct = normalizeStorageKey(object?.originalKey);
	if (direct) return direct;
	const key = normalizeStorageKey(object?.key);
	return key || null;
};

export const resolveDisplayKey = (object?: StorageObject | null): string | null =>
	resolveCompressedKey(object) ?? resolveOriginalKey(object);

export const formatFileSize = (size?: number | null): string => {
	if (typeof size !== "number" || Number.isNaN(size) || size < 0) {
		return "-";
	}
	const units = ["o", "Ko", "Mo", "Go", "To"];
	let value = size;
	let unitIndex = 0;
	while (value >= 1024 && unitIndex < units.length - 1) {
		value /= 1024;
		unitIndex++;
	}
	const formatted = unitIndex === 0 ? value.toFixed(0) : value < 10 ? value.toFixed(1).replace(".", ",") : value.toFixed(0);
	return `${formatted} ${units[unitIndex]}`;
};

export const extractErrorMessage = (error: unknown): string | null => {
	if (error && typeof error === "object" && "response" in error && (error as any).response) {
		const response = (error as any).response;
		let message = response?.data?.message ?? response?.data?.error ?? response?.data ?? response?.statusText ?? null;
		if (typeof message === "string") {
			const truncated = message.split(" at BCC.ADMIN")[0]?.trim();
			return truncated?.length ? truncated : message;
		}
		return null;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return null;
};

export const resolvePathValue = (value?: string | null) => normalizePath(value) ?? "__root__";

// ---------------------------------------------------------------------------
// Type de fichier (icône + libellé) et vignettes
// ---------------------------------------------------------------------------

export type FileKind = "image" | "pdf" | "document" | "other";

/** Classe le fichier via son content-type, avec repli sur l'extension (certains
 *  navigateurs envoient un type vide ou générique à l'upload). */
export const resolveFileKind = (object?: StorageObject | null): FileKind => {
	const type = (object?.contentType ?? "").toLowerCase();
	if (type.startsWith("image/")) return "image";
	if (type.includes("pdf")) return "pdf";
	if (type.includes("msword") || type.includes("wordprocessingml") || type.includes("presentation") || type.includes("powerpoint") || type.includes("rtf") || type.startsWith("text/")) {
		return "document";
	}
	const ext = ((object?.fileName ?? object?.key ?? "").split(".").pop() ?? "").toLowerCase();
	if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"].includes(ext)) return "image";
	if (ext === "pdf") return "pdf";
	if (PUBLICATION_EXTENSIONS.has(ext)) return "document";
	return "other";
};

export const fileKindLabel = (kind: FileKind): string => {
	switch (kind) {
		case "image":
			return "Image";
		case "pdf":
			return "PDF";
		case "document":
			return "Document";
		default:
			return "Fichier";
	}
};

// ---------------------------------------------------------------------------
// Tri de la vue liste / grille
// ---------------------------------------------------------------------------

const sortableName = (object: StorageObject): string => (object.title ?? object.fileName ?? object.key ?? "").toLocaleLowerCase("fr-FR");

const sortableDate = (object: StorageObject): number => {
	const date = parseApiDate(object.updatedAt ?? object.createdAt ?? null);
	return date ? date.getTime() : 0;
};

/** Trie les fichiers du dossier courant (nom / taille / date, asc ou desc). Copie, ne mute pas. */
export const sortStorageObjects = (objects: StorageObject[], key: SortKey, dir: SortDir): StorageObject[] => {
	const mult = dir === "asc" ? 1 : -1;
	return [...objects].sort((a, b) => {
		if (key === "size") {
			return (Number(a.sizeBytes ?? 0) - Number(b.sizeBytes ?? 0)) * mult;
		}
		if (key === "date") {
			return (sortableDate(a) - sortableDate(b)) * mult;
		}
		return sortableName(a).localeCompare(sortableName(b), "fr-FR") * mult;
	});
};

/** Les dossiers restent triés par nom, toujours ascendant : prévisible quel que soit le tri fichiers. */
export const sortFolders = (folders: StorageFolder[]): StorageFolder[] =>
	[...folders].sort((a, b) => (a.name ?? a.path ?? "").toLocaleLowerCase("fr-FR").localeCompare((b.name ?? b.path ?? "").toLocaleLowerCase("fr-FR"), "fr-FR"));

/** Somme des tailles des fichiers chargés (la page courante) : l'en-tête précise « ≥ » si hasMore. */
export const sumSizes = (objects: StorageObject[]): number =>
	objects.reduce((total, object) => total + Math.max(0, Number(object.sizeBytes ?? 0)), 0);

// ---------------------------------------------------------------------------
// « Utilisé par » : croisement avec le rapport du scan de médias orphelins
// ---------------------------------------------------------------------------

/** Retrouve l'entrée du rapport pour un fichier : par id d'abord, sinon par n'importe laquelle
 *  de ses trois clés (key / originalKey / compressedKey), insensible à la casse. */
export const findUsageEntry = (
	report: MediaOrphanReportDto | null | undefined,
	object: StorageObject | null | undefined
): MediaOrphanFileDto | null => {
	if (!report?.files?.length || !object) return null;
	if (object.id) {
		const byId = report.files.find((file) => file.id === object.id);
		if (byId) return byId;
	}
	const keys = new Set(
		[object.key, object.originalKey, object.compressedKey]
			.map((key) => normalizeStorageKey(key).toLowerCase())
			.filter(Boolean)
	);
	if (!keys.size) return null;
	return report.files.find((file) => file.key && keys.has(normalizeStorageKey(file.key).toLowerCase())) ?? null;
};

/** Route de l'éditeur pour un contenu référençant : page → /page/{id}, avatar → /account/{id},
 *  sinon l'éditeur générique /content/{schemaKey}/{id}. */
export const editorPathForUsage = (usage: MediaOrphanUsageDto): string | null => {
	const contentId = (usage.contentId ?? "").trim();
	if (!contentId) return null;
	const schemaKey = (usage.schemaKey ?? "").trim();
	if (schemaKey === "page") return `/page/${encodeURIComponent(contentId)}`;
	if (schemaKey === "user") return `/account/${encodeURIComponent(contentId)}`;
	if (!schemaKey) return null;
	return `/content/${encodeURIComponent(schemaKey)}/${encodeURIComponent(contentId)}`;
};

// ---------------------------------------------------------------------------
// Divers
// ---------------------------------------------------------------------------

/** Détecte la destination d'upload d'un fichier : image/* → images, extensions bureautiques → publications. */
export const detectUploadKind = (file: File): UploadKind | null => {
	if ((file.type ?? "").startsWith("image/")) return "image";
	const ext = (file.name.split(".").pop() ?? "").toLowerCase();
	if (file.type === "application/pdf" || PUBLICATION_EXTENSIONS.has(ext)) return "publication";
	return null;
};

/** Déclenche le téléchargement navigateur d'un blob sous le nom donné (révoque l'URL ensuite). */
export const triggerBlobDownload = (blob: Blob, fileName: string) => {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = fileName || "fichier";
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	window.setTimeout(() => URL.revokeObjectURL(url), 5000);
};
